/**
 * aeterna/neural/liquid.ts — Liquid Neural Network
 *
 * Continuous-time ODE neurons for the Aeterna's personality state.
 * Each neuron's time constant τ is input-dependent — the dynamics
 * themselves encode temporal patterns, not just the weights.
 *
 * 4 neurons model the 4 personality dimensions:
 *   patience, curiosity, warmth, wit
 *
 * A patient conversation slowly shifts the state. Rudeness causes
 * rapid τ contraction — the personality responds faster to negative
 * signals than positive ones (negativity bias, as in real relationships).
 *
 * This produces organic personality drift without any LLM call.
 * The personality is a dynamical system, not a lookup table.
 *
 * MIT CSAIL showed 19 LNN neurons match LSTM performance.
 * We use 8: 4 for personality + 4 for cross-coupling dynamics.
 */

import { zeros, mulberry32, gaussianSeeded } from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Number of personality dimensions */
const N_PERSONALITY = 4;
/** Total neurons (personality + coupling) */
const N_NEURONS = 8;

/** Labels for the 4 personality dimensions */
export const PERSONALITY_DIMS = ["patience", "curiosity", "warmth", "wit"] as const;
export type PersonalityDim = typeof PERSONALITY_DIMS[number];

// ─── Liquid Neuron ───────────────────────────────────────────────────────────

interface LiquidNeuronParams {
  /** Base time constant (how slowly the neuron responds) */
  tauBase: number;
  /** Input weight */
  w: number;
  /** Bias */
  b: number;
  /** Cross-coupling weights from other neurons */
  coupling: Float32Array;
  /** Sensitivity modulator for τ (how much input affects response speed) */
  tauSensitivity: number;
  /** Rest state (equilibrium value) */
  rest: number;
}

// ─── Liquid Network ──────────────────────────────────────────────────────────

export class LiquidPersonality {
  /** Current state of each neuron */
  private states: Float32Array;
  /** Neuron parameters */
  private neurons: LiquidNeuronParams[];
  /** Running velocity (for smooth reporting) */
  private velocities: Float32Array;

  constructor(seed = 777) {
    const rng = mulberry32(seed);

    this.states = new Float32Array(N_NEURONS);
    this.velocities = new Float32Array(N_NEURONS);
    this.neurons = [];

    // Initialize all neurons at 0.5 (neutral personality)
    this.states.fill(0.5);

    for (let i = 0; i < N_NEURONS; i++) {
      const coupling = new Float32Array(N_NEURONS);
      for (let j = 0; j < N_NEURONS; j++) {
        if (i !== j) coupling[j] = gaussianSeeded(rng) * 0.1;
      }

      const isPrimary = i < N_PERSONALITY;
      this.neurons.push({
        tauBase: isPrimary ? 120 + gaussianSeeded(rng) * 30 : 60 + gaussianSeeded(rng) * 20,
        w: isPrimary ? 0.8 + gaussianSeeded(rng) * 0.2 : 0.5 + gaussianSeeded(rng) * 0.3,
        b: gaussianSeeded(rng) * 0.1,
        coupling,
        tauSensitivity: isPrimary ? 0.5 : 0.3,
        rest: 0.5,
      });
    }

    // Specific personality coupling structure:
    // patience ↔ warmth (positive), curiosity ↔ wit (positive)
    // tension between patience and curiosity (mild negative)
    this.neurons[0].coupling[2] = 0.08;  // patience → warmth
    this.neurons[2].coupling[0] = 0.08;  // warmth → patience
    this.neurons[1].coupling[3] = 0.06;  // curiosity → wit
    this.neurons[3].coupling[1] = 0.06;  // wit → curiosity
    this.neurons[0].coupling[1] = -0.03; // patience ↔ curiosity (mild tension)
    this.neurons[1].coupling[0] = -0.03;
  }

  /**
   * Step the liquid network forward by dt seconds.
   *
   * inputs: 4-dim vector of current interaction signals
   *   [0] = patience signal (positive = patient interaction)
   *   [1] = curiosity signal (positive = exploratory interaction)
   *   [2] = warmth signal (positive = kind interaction)
   *   [3] = wit signal (positive = playful interaction)
   *
   * Each signal is typically in [-1, 1].
   *
   * The ODE per neuron:
   *   dx/dt = (-x + rest + tanh(w·u + Σ coupling_j·x_j + b)) / τ(x, u)
   *   τ(x, u) = τ_base / (1 + sensitivity · |u|)
   *
   * This means:
   *   - Large input → small τ → fast response (the personality reacts quickly to strong signals)
   *   - No input → large τ → slow drift back to rest
   *   - Negative signals contract τ MORE than positive (asymmetric sensitivity)
   */
  step(inputs: Float32Array, dt: number): void {
    // 4th-order Runge-Kutta with adaptive sub-stepping.
    // Critical: when the tab is backgrounded, requestAnimationFrame resumes
    // with dt of potentially *seconds*. A single RK4 step at dt=2.0 will
    // blow up the ODE (personality jumps to rail). Sub-step at ≤20ms.
    const MAX_STEP = 0.02; // 20ms max per RK4 step
    const maxDt = Math.min(dt, 2.0); // absolute ceiling: 2 seconds of drift
    let remaining = maxDt;

    while (remaining > 1e-6) {
      const h = Math.min(remaining, MAX_STEP);
      this.stepRK4(inputs, h);
      remaining -= h;
    }
  }

  /** Single RK4 integration step (internal, always called with safe dt) */
  private stepRK4(inputs: Float32Array, h: number): void {
    const N = N_NEURONS;

    // Store current state for RK4 stages
    const x0 = new Float32Array(this.states);

    // Stage 1: k1 = f(x0)
    const k1 = this.computeDerivatives(x0, inputs);

    // Stage 2: k2 = f(x0 + h/2 * k1)
    const x1 = new Float32Array(N);
    for (let i = 0; i < N; i++) x1[i] = x0[i] + 0.5 * h * k1[i];
    const k2 = this.computeDerivatives(x1, inputs);

    // Stage 3: k3 = f(x0 + h/2 * k2)
    const x2 = new Float32Array(N);
    for (let i = 0; i < N; i++) x2[i] = x0[i] + 0.5 * h * k2[i];
    const k3 = this.computeDerivatives(x2, inputs);

    // Stage 4: k4 = f(x0 + h * k3)
    const x3 = new Float32Array(N);
    for (let i = 0; i < N; i++) x3[i] = x0[i] + h * k3[i];
    const k4 = this.computeDerivatives(x3, inputs);

    // Combine: x(t+h) = x(t) + h/6 * (k1 + 2*k2 + 2*k3 + k4)
    for (let i = 0; i < N; i++) {
      this.velocities[i] = (k1[i] + 2 * k2[i] + 2 * k3[i] + k4[i]) / 6;
      this.states[i] = x0[i] + h * this.velocities[i];

      // Soft clamp to [0.05, 0.95]
      this.states[i] = Math.max(0.05, Math.min(0.95, this.states[i]));
    }
  }

  /** Compute dx/dt for all neurons at a given state (used by RK4 stages) */
  private computeDerivatives(states: Float32Array, inputs: Float32Array): Float32Array {
    const N = N_NEURONS;
    const dx = new Float32Array(N);

    for (let i = 0; i < N; i++) {
      const n = this.neurons[i];
      const u = i < inputs.length ? inputs[i] : 0;

      // Input-dependent time constant
      // Negative signals cause faster response (negativity bias)
      const inputMag = Math.abs(u);
      const negativityBoost = u < 0 ? 1.5 : 1.0;
      const tau = n.tauBase / (1 + n.tauSensitivity * inputMag * negativityBoost);

      // Cross-coupling from other neurons
      let couplingSum = 0;
      for (let j = 0; j < N; j++) {
        couplingSum += n.coupling[j] * states[j];
      }

      // ODE: dx/dt = (-x + rest + tanh(w*u + coupling + b)) / tau
      const activation = Math.tanh(n.w * u + couplingSum + n.b);
      dx[i] = (-(states[i] - n.rest) + activation) / tau;
    }

    return dx;
  }

  /** Get the 4 personality dimensions */
  getPersonality(): { patience: number; curiosity: number; warmth: number; wit: number } {
    return {
      patience:  this.states[0],
      curiosity: this.states[1],
      warmth:    this.states[2],
      wit:       this.states[3],
    };
  }

  /** Get the raw state vector (all 8 neurons) for Hopfield storage */
  getFullState(): Float32Array {
    return new Float32Array(this.states);
  }

  /** Set personality state directly (e.g., from saved state) */
  setState(personality: { patience: number; curiosity: number; warmth: number; wit: number }): void {
    this.states[0] = personality.patience;
    this.states[1] = personality.curiosity;
    this.states[2] = personality.warmth;
    this.states[3] = personality.wit;
  }

  /** Get velocity of change for each dimension (how fast it's shifting) */
  getVelocity(): Float32Array {
    return new Float32Array(this.velocities);
  }

  /** Check if personality is currently stable (low velocity) */
  isStable(threshold = 0.001): boolean {
    for (let i = 0; i < N_PERSONALITY; i++) {
      if (Math.abs(this.velocities[i]) > threshold) return false;
    }
    return true;
  }

  /**
   * Compute the "felt shift" — how much personality has moved from a reference point.
   * This is what the Aeterna uses to know "something changed between us."
   */
  feltShift(reference: { patience: number; curiosity: number; warmth: number; wit: number }): number {
    const dp = this.states[0] - reference.patience;
    const dc = this.states[1] - reference.curiosity;
    const dw = this.states[2] - reference.warmth;
    const di = this.states[3] - reference.wit;
    return Math.sqrt(dp * dp + dc * dc + dw * dw + di * di);
  }

  /**
   * Apply an interaction signal directly.
   * Maps InteractionSignal types to the 4-dim input vector.
   */
  applySignal(signal: string, intensity = 1.0): void {
    const inputs = new Float32Array(4);

    switch (signal) {
      case "kind":
        inputs[2] = 0.4 * intensity;    // warmth ++
        inputs[0] = 0.2 * intensity;    // patience +
        break;
      case "dismissive":
        inputs[2] = -0.6 * intensity;   // warmth -- (faster due to negativity bias)
        inputs[3] = -0.3 * intensity;   // wit -
        break;
      case "philosophical":
        inputs[1] = 0.5 * intensity;    // curiosity ++
        inputs[0] = 0.3 * intensity;    // patience +
        break;
      case "playful":
        inputs[3] = 0.5 * intensity;    // wit ++
        inputs[2] = 0.15 * intensity;   // warmth +
        break;
      case "neglectful":
        inputs[2] = -0.2 * intensity;   // warmth -
        inputs[0] = 0.1 * intensity;    // patience + (learned to wait)
        break;
      case "creative":
        inputs[1] = 0.3 * intensity;    // curiosity +
        inputs[3] = 0.2 * intensity;    // wit +
        break;
      case "positive_feedback":
        inputs[2] = 0.3 * intensity;    // warmth +
        inputs[0] = 0.1 * intensity;    // patience +
        inputs[1] = 0.1 * intensity;    // curiosity + (what worked? explore more)
        break;
      case "negative_feedback":
        inputs[2] = -0.15 * intensity;  // warmth - (gentle)
        inputs[0] = 0.2 * intensity;    // patience + (learn to be more careful)
        break;
    }

    // Step with a large effective dt to apply the signal over ~1 interaction period
    this.step(inputs, 1.0);
  }

  /**
   * Policy gradient that targets the ODE ATTRACTOR, not dynamic state.
   *
   * The ODE pulls state toward n.rest (the attractor / equilibrium).
   * Nudging state directly (via applySignal) is immediately fought by
   * computeDerivatives() on the next tick. Instead, we move the
   * attractor itself — the ODE then naturally evolves toward
   * the reinforced configuration. Dynamics and learning cooperate.
   *
   * @param reward Positive = reinforce current attractor, negative = contract toward 0.5
   * @param lr Learning rate (default 0.0005 — attractors should change over weeks)
   */
  applyPolicyGradient(reward: number, lr = 0.0005): void {
    const direction = Math.sign(reward);
    const magnitude = Math.abs(reward) * lr;

    for (let i = 0; i < N_PERSONALITY; i++) {
      const n = this.neurons[i];
      if (direction > 0) {
        // Positive reward: move attractor toward current state
        // (reinforce whatever personality configuration worked)
        n.rest += magnitude * (this.states[i] - n.rest);
      } else {
        // Negative reward: contract attractor toward neutral (0.5)
        // (don't commit to what isn't working)
        n.rest += magnitude * (0.5 - n.rest);
      }
      // Keep attractor in valid range
      n.rest = Math.max(0.1, Math.min(0.9, n.rest));
    }
  }

  /** Get the rest state (attractor) for each personality dim */
  getRestState(): Float32Array {
    const rest = new Float32Array(N_PERSONALITY);
    for (let i = 0; i < N_PERSONALITY; i++) rest[i] = this.neurons[i].rest;
    return rest;
  }

  // ─── Serialization ───────────────────────────────────────────────────────

  serialize(): { states: number[]; velocities: number[]; restStates: number[] } {
    const restStates: number[] = [];
    for (let i = 0; i < N_NEURONS; i++) restStates.push(this.neurons[i].rest);
    return {
      states: Array.from(this.states),
      velocities: Array.from(this.velocities),
      restStates,
    };
  }

  loadState(data: { states: number[]; velocities: number[]; restStates?: number[] }): void {
    for (let i = 0; i < N_NEURONS; i++) {
      this.states[i] = data.states[i] ?? 0.5;
      this.velocities[i] = data.velocities[i] ?? 0;
      if (data.restStates && data.restStates[i] !== undefined) {
        this.neurons[i].rest = data.restStates[i];
      }
    }
  }
}
